/**
 * localStorage-based image caching utility for Dance Localé
 * Caches organization profile images to prevent redundant API calls on page navigation
 * 
 * CACHING STRATEGY:
 * - Images are cached for 1 year (effectively permanent)
 * - Cache is only cleared when user actually updates their profile images:
 *   1. Facebook integration import/refresh (may update profile/cover images)
 *   2. Manual profile image uploads (future feature)
 * - This approach prevents unnecessary API calls since profile images rarely change
 * 
 * CORS-FREE IMPLEMENTATION (2025):
 * - Uses backend Base64 API endpoints instead of S3 presigned URLs
 * - Eliminates CORS issues by getting Base64 data directly from backend
 * - Same approach as mobile app (getThumbnailBase64 service)
 */

import webApiClient from '../api/client';

const CACHE_PREFIX = 'dl_image_cache_';
const CACHE_EXPIRY_DAYS = 365; // Cache images for 1 year (effectively permanent unless manually cleared)
const MAX_CACHE_SIZE = 50; // Maximum number of cached images

// In-memory layer: eliminates localStorage read race conditions and makes repeat access instant
const memoryCache = new Map(); // `${type}_${imageKey}` → base64 string

// Deduplicates concurrent fetches for the same key across all component instances
const inFlightRequests = new Map(); // `${type}_${imageKey}` → Promise<string|null>

/**
 * Generate cache key for an image
 */
const getCacheKey = (imageKey, type = 'profile') => {
    return `${CACHE_PREFIX}${type}_${imageKey}`;
};

/**
 * Generate metadata key for cache entry
 */
const getMetadataKey = (imageKey, type = 'profile') => {
    return `${CACHE_PREFIX}meta_${type}_${imageKey}`;
};

/**
 * Check if cached image is still valid
 * Profile images are cached until manually cleared (when user updates them)
 */
const isCacheValid = (metadata) => {
    if (!metadata || !metadata.timestamp) return false;
    
    const now = Date.now();
    const cacheAge = now - metadata.timestamp;
    const maxAge = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000; // Convert days to milliseconds
    
    return cacheAge < maxAge;
};

/**
 * Clean up old cache entries to prevent localStorage from filling up
 */
const cleanupCache = () => {
    try {
        const cacheEntries = [];
        
        // Find all cache entries
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_PREFIX)) {
                const value = localStorage.getItem(key);
                if (value) {
                    try {
                        const parsed = JSON.parse(value);
                        cacheEntries.push({
                            key,
                            timestamp: parsed.timestamp || 0
                        });
                    } catch (e) {
                        // Remove invalid entries
                        localStorage.removeItem(key);
                    }
                }
            }
        }
        
        // Sort by timestamp (oldest first) and remove excess entries
        cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
        
        if (cacheEntries.length > MAX_CACHE_SIZE) {
            const toRemove = cacheEntries.slice(0, cacheEntries.length - MAX_CACHE_SIZE);
            toRemove.forEach(entry => {
                localStorage.removeItem(entry.key);
                // Also remove corresponding metadata
                const metaKey = entry.key.replace(CACHE_PREFIX, `${CACHE_PREFIX}meta_`);
                localStorage.removeItem(metaKey);
            });
        }
    } catch (error) {
        console.warn('Error cleaning up image cache:', error);
    }
};

/**
 * Get Base64 image from backend API - bypasses CORS issues entirely
 */
const getImageAsBase64 = async (imageKey, type) => {
    const endpoint = type === 'overlay' ?
        '/api/v2/org/profile-overlay-image-base64' :
        '/api/v2/org/profile-image-base64';

    const response = await webApiClient.get(endpoint, { imageKey });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Image not found');
        }
        throw new Error(response.data?.message || `API request failed: ${response.status}`);
    }

    return response.data.imageBase64; // Returns "data:image/jpeg;base64,..." format
};

/**
 * Store image data in cache with metadata - supports both Base64 data and API fetching
 */
export const cacheImage = async (imageKey, dataUrlOrIgnored, type = 'profile') => {
    try {
        const cacheKey = getCacheKey(imageKey, type);
        const metadataKey = getMetadataKey(imageKey, type);
        
        let dataUrl;
        
        // Check if dataUrlOrIgnored is already a Base64 data URL (new approach)
        if (dataUrlOrIgnored && dataUrlOrIgnored.startsWith('data:image/')) {
            dataUrl = dataUrlOrIgnored;
        } else {
            // Fall back to API fetch (for legacy calls or when no data provided)
            dataUrl = await getImageAsBase64(imageKey, type);
        }
        
        const metadata = {
            timestamp: Date.now(),
            imageKey,
            type,
            originalUrl: dataUrlOrIgnored, // Keep reference for debugging
            cached: 'base64', // Mark that this entry contains Base64 data
            source: dataUrlOrIgnored && dataUrlOrIgnored.startsWith('data:image/') ? 'direct' : 'api'
        };
        
        memoryCache.set(`${type}_${imageKey}`, dataUrl);
        localStorage.setItem(cacheKey, dataUrl); // Store Base64 data URL
        localStorage.setItem(metadataKey, JSON.stringify(metadata));
        // console.log(`[IMG-DEBUG] Cached: type=${type} key=${imageKey} size=${Math.round(dataUrl.length / 1024)}KB source=${metadata.source}`);

        // Clean up old entries periodically
        if (Math.random() < 0.1) { // 10% chance to trigger cleanup
            cleanupCache();
        }

        return true;
    } catch (error) {
        console.warn(`Failed to cache image: ${type}/${imageKey}`, error);
        return false;
    }
};

/**
 * Retrieve cached image data/URL if valid — checks in-memory layer first, then localStorage
 */
export const getCachedImage = (imageKey, type = 'profile') => {
    const memKey = `${type}_${imageKey}`;

    // In-memory layer: instant, no race conditions between render cycles
    if (memoryCache.has(memKey)) {
        // console.log(`[IMG-DEBUG] Memory HIT: type=${type} key=${imageKey}`);
        return memoryCache.get(memKey);
    }

    try {
        const cacheKey = getCacheKey(imageKey, type);
        const metadataKey = getMetadataKey(imageKey, type);

        const cachedData = localStorage.getItem(cacheKey);
        const metadataStr = localStorage.getItem(metadataKey);

        if (!cachedData || !metadataStr) {
            // Diagnose WHY this missed: was either side present? What similar keys exist?
            const dataPresent = cachedData !== null;
            const metaPresent = metadataStr !== null;
            const similarKeys = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.startsWith(`${CACHE_PREFIX}${type}_`) && k.includes(imageKey.split('_')[1] || imageKey)) {
                    similarKeys.push(k.replace(CACHE_PREFIX, ''));
                }
            }
            // console.log(
            //     `[IMG-DEBUG] Cache MISS: type=${type} key=${imageKey}`,
            //     `\n  dataPresent=${dataPresent} metaPresent=${metaPresent}`,
            //     `\n  fullDataKey=${cacheKey}`,
            //     `\n  fullMetaKey=${metadataKey}`,
            //     `\n  similarKeysInStorage=${JSON.stringify(similarKeys)}`
            // );
            return null;
        }

        let metadata;
        try {
            metadata = JSON.parse(metadataStr);
        } catch (parseErr) {
            // console.warn(`[IMG-DEBUG] Cache MISS (metadata JSON parse failed): type=${type} key=${imageKey}`, parseErr);
            return null;
        }

        // Check if this is a Base64 data URL (new format) or S3 URL (legacy format)
        const startsWithData = cachedData.startsWith('data:');
        const isBase64Cache = metadata.cached === 'base64' || startsWithData;

        if (isBase64Cache) {
            // Populate memory layer so subsequent reads skip localStorage entirely
            memoryCache.set(memKey, cachedData);
            // console.log(`[IMG-DEBUG] Cache HIT: type=${type} key=${imageKey} size=${Math.round(cachedData.length / 1024)}KB`);
            return cachedData;
        } else {
            // Legacy URL cache - check if still valid
            if (!isCacheValid(metadata)) {
                // console.log(
                //     `[IMG-DEBUG] Cache MISS (legacy URL expired): type=${type} key=${imageKey}`,
                //     `\n  metadata.cached=${metadata.cached} startsWithData=${startsWithData}`,
                //     `\n  cachedDataPrefix=${cachedData.slice(0, 30)}`,
                //     `\n  age=${Math.round((Date.now() - metadata.timestamp) / (1000 * 60 * 60 * 24))}days`
                // );
                localStorage.removeItem(cacheKey);
                localStorage.removeItem(metadataKey);
                return null;
            }

            // console.log(
            //     `[IMG-DEBUG] Cache HIT (legacy URL): type=${type} key=${imageKey}`,
            //     `\n  metadata.cached=${metadata.cached} startsWithData=${startsWithData}`,
            //     `\n  cachedDataPrefix=${cachedData.slice(0, 30)}`
            // );
            return cachedData;
        }
    } catch (error) {
        console.warn('Error retrieving cached image:', error);
        return null;
    }
};

/**
 * Clear specific image from cache
 */
export const clearCachedImage = (imageKey, type = 'profile') => {
    try {
        memoryCache.delete(`${type}_${imageKey}`);
        const cacheKey = getCacheKey(imageKey, type);
        const metadataKey = getMetadataKey(imageKey, type);

        localStorage.removeItem(cacheKey);
        localStorage.removeItem(metadataKey);

        return true;
    } catch (error) {
        console.warn('Error clearing cached image:', error);
        return false;
    }
};

/**
 * Clear all cached images (use when user updates profile images)
 */
export const clearAllCachedImages = () => {
    try {
        let clearedCount = 0;
        const keysToRemove = [];
        
        // Find all cache keys
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        
        // Remove all cache entries
        keysToRemove.forEach(key => {
            localStorage.removeItem(key);
            clearedCount++;
        });

        memoryCache.clear();
        inFlightRequests.clear();

        console.log(`Cleared ${clearedCount} cached images (profile images may have been updated)`);
        return clearedCount;
    } catch (error) {
        console.warn('Error clearing image cache:', error);
        return 0;
    }
};

/**
 * Clear cached images for a specific organization (when user updates their own images)
 */
export const clearOrgCachedImages = (organisationId) => {
    try {
        let clearedCount = 0;
        const keysToRemove = [];

        // Find cache keys for this organization
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_PREFIX)) {
                // Check if this is for the current org (you might want to include organisationId in cache key)
                keysToRemove.push(key);
            }
        }

        // Remove org-specific cache entries
        keysToRemove.forEach(key => {
            localStorage.removeItem(key);
            clearedCount++;
        });

        console.log(`Cleared ${clearedCount} cached images for organization ${organisationId}`);
        return clearedCount;
    } catch (error) {
        console.warn('Error clearing org image cache:', error);
        return 0;
    }
};

/**
 * Get cache statistics for debugging
 */
export const getCacheStats = () => {
    try {
        let profileImages = 0;
        let overlayImages = 0;
        let base64Cached = 0;
        let urlCached = 0;
        let totalSize = 0;
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_PREFIX)) {
                const value = localStorage.getItem(key);
                if (value) {
                    totalSize += key.length + value.length;
                    
                    // Count by type
                    if (key.includes('_profile_')) {
                        profileImages++;
                    } else if (key.includes('_overlay_')) {
                        overlayImages++;
                    }
                    
                    // Count by cache format
                    if (key.includes('_meta_')) {
                        // Skip metadata entries for format counting
                        continue;
                    }
                    
                    if (value.startsWith('data:')) {
                        base64Cached++;
                    } else {
                        urlCached++;
                    }
                }
            }
        }
        
        return {
            profileImages,
            overlayImages,
            totalEntries: profileImages + overlayImages,
            base64Cached,
            urlCached: urlCached,
            estimatedSizeKB: Math.round(totalSize / 1024),
            estimatedSizeMB: Math.round(totalSize / (1024 * 1024) * 100) / 100
        };
    } catch (error) {
        console.warn('Error getting cache stats:', error);
        return null;
    }
};

/**
 * Enhanced fetch function that uses cache-first strategy
 */
export const fetchImageWithCache = async (imageKey, apiEndpoint, type = 'profile') => {
    // First, try to get from cache
    const cachedUrl = getCachedImage(imageKey, type);
    if (cachedUrl) {
        return {
            success: true,
            imageUrl: cachedUrl,
            fromCache: true
        };
    }
    
    // Cache miss, fetch from API
    try {
        console.log(`Fetching image from API: ${type}/${imageKey}`);
        
        // This would be called from the component with the actual API client
        // We return a function that the component can call with their API client
        return {
            success: false,
            needsFetch: true,
            imageKey,
            type
        };
    } catch (error) {
        console.error('Error in fetchImageWithCache:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Fetch an image with full deduplication: in-memory → localStorage → single in-flight fetch.
 *
 * fetchFn must return Promise<string|null> where the string is a base64 data URL.
 * Concurrent calls for the same key join the existing promise instead of firing new requests.
 * The returned promise always resolves (never rejects) — null means no image available.
 */
export const getOrFetchImage = (imageKey, type = 'profile', fetchFn) => {
    const memKey = `${type}_${imageKey}`;

    if (inFlightRequests.has(memKey)) {
        return inFlightRequests.get(memKey);
    }

    const promise = fetchFn()
        .then(async base64 => {
            if (base64) {
                await cacheImage(imageKey, base64, type);
            }
            return base64 || null;
        })
        .catch(() => null)
        .finally(() => inFlightRequests.delete(memKey));

    inFlightRequests.set(memKey, promise);
    return promise;
};

/**
 * Debug helper to inspect cache in browser console
 */
export const debugCache = () => {
    const stats = getCacheStats();
    console.log('Image Cache Debug Stats:', stats);

    console.log('\nCache Entries:');
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
            const value = localStorage.getItem(key);
            if (key.includes('_meta_')) {
                const metadata = JSON.parse(value);
                console.log(`   ${key}: ${metadata.cached} cache (${new Date(metadata.timestamp).toLocaleString()})`);
            } else {
                const isBase64 = value.startsWith('data:');
                const size = Math.round(value.length / 1024);
                console.log(`   ${key}: ${isBase64 ? 'Base64' : 'URL'} (${size}KB)`);
            }
        }
    }
    
    return stats;
};

/**
 * Convert a base64 data URL to a Blob URL for rendering.
 * Fixes WebKit/Safari iOS limitation where large data: URIs won't render in <img src>.
 * Blob URLs have no size restrictions and are fully supported on all browsers.
 *
 * IMPORTANT: The returned URL must be revoked with URL.revokeObjectURL() when no longer needed
 * to avoid memory leaks. Use in a useEffect cleanup or component unmount.
 */
export const base64ToBlobUrl = (base64DataUrl) => {
    try {
        const [header, data] = base64DataUrl.split(',');
        const mimeMatch = header.match(/:(.*?);/);
        if (!mimeMatch || !data) {
            // console.warn('[IMG-DEBUG] base64ToBlobUrl: bad format, falling back to data URL', { header });
            return base64DataUrl; // fallback: return original
        }

        const mimeType = mimeMatch[1];
        const binary = atob(data);
        const array = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            array[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([array], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        // console.log(`[IMG-DEBUG] Blob URL created: mime=${mimeType} inputSize=${Math.round(base64DataUrl.length / 1024)}KB blobUrl=${blobUrl.substring(0, 40)}...`);
        return blobUrl;
    } catch (err) {
        // console.warn('[IMG-DEBUG] base64ToBlobUrl: conversion failed, using original', err);
        return base64DataUrl;
    }
};

// Make debug function available globally for console testing
if (typeof window !== 'undefined') {
    window.debugImageCache = debugCache;
}

export default {
    cacheImage,
    getCachedImage,
    getOrFetchImage,
    clearCachedImage,
    clearAllCachedImages,
    clearOrgCachedImages,
    getCacheStats,
    fetchImageWithCache,
    debugCache
};